<script setup>
/**
 * PlanQuestionnaireForm - A reusable component for displaying and handling plan questionnaire forms
 * 
 * This component can be used in both the main PlanQuestionnaire page and the RenewEmployerModal
 * with different configurations.
 * 
 * @prop {Array} questions - The list of questions to display
 * @prop {String} mainKey - The key for accessing questions in the store
 * @prop {Boolean} autoSave - Whether to auto-save when a field changes (default: true)
 * @prop {Boolean} isRenewPlanYearForm - Whether to show employer name, EIN, and plan start date fields (default: false)
 * @prop {Object} employerData - The employer data object
 * @prop {Object} planYearData - The plan year data object
 * 
 * @emits {save} - Emitted when the form is saved
 * @emits {validate} - Emitted when the form needs validation
 */
import { ref, defineProps, defineEmits, computed } from 'vue'
import { vMaska } from 'maska'
import TextWithDefinitions from '../TextWithDefinitions.vue'
import { markCheckbox } from '../../utils'

const props = defineProps({
  // The list of questions to display
  questions: {
    type: Array,
    required: false,
    default: () => []
  },
  // Whether to auto-save when a field changes
  autoSave: {
    type: Boolean,
    default: true
  },
  // Whether to show employer name, EIN, and plan start date fields
  isRenewPlanYearForm: {
    type: Boolean,
    default: false
  },
  // The employer data object
  employerData: {
    type: Object,
    required: false,
    default: () => ({})
  },
  // The plan year data object
  planYearData: {
    type: Object,
    required: false,
    default: () => ({ plan_start_date: null })
  },
  // Form validation functions
  isFieldValid: {
    type: Function,
    default: () => true
  },
  isFieldTouched: {
    type: Function,
    default: () => false
  }
})

const emit = defineEmits(['save', 'validate'])

// Raw mask value for input masking
const rawMaskVal = ref({})

// Handle field change
const handleFieldChange = () => {
  if (props.autoSave) {
    emit('save')
  }
}

// Handle checkbox selection
const handleCheckboxChange = (question, optionIndex) => {
  markCheckbox(question, optionIndex)
  handleFieldChange()
}

const handleDropdownChange = (question, event) => {
  const selectedValue = event.target.value

  if (question.isOptional && selectedValue === '') {
    question.answer = null
  } else {
    question.answer = Number(selectedValue)
  }

  handleFieldChange()
}

// Check if all required fields are valid
const isFormValid = computed(() => {
  // Add your validation logic here if needed
  return true
})
</script>

<template>
  <div>
    <!-- Employer fields (optional) -->
    <template v-if="!isRenewPlanYearForm">
      <div class="mb-3 pt-2">
        <div class="question-title text-14 mb-1">
          <strong>Employer Legal Name</strong>
        </div>
        <input
          id="employerNamePQ"
          type="text"
          class="form-control text-16 w-50"
          maxlength="150"
          v-model="employerData.name"
          @change="handleFieldChange"
          :class="{ 'is-invalid': !isFieldValid('name') && isFieldTouched('name') }" />
      </div>
      <div class="d-flex flex-row align-items-center flex-wrap">
        <div class="mb-3">
          <div class="question-title text-14 mb-1">
            <strong>Employer Federal Tax ID Number</strong>
          </div>
          <input
            id="employerEINPQ"
            type="text"
            class="form-control text-16 tax-id-input"
            v-model="employerData.ein"
            @change="handleFieldChange"
            :class="{ 'is-invalid': !isFieldValid('ein') && isFieldTouched('ein') }"
            v-maska="rawMaskVal"
            data-maska="##-#######"
            placeholder="e.g., 12-3456789" />
        </div>
        <div class="ms-4 mb-3">
          <div class="question-title text-14 mb-1">
            <strong>Plan Start Date</strong>
          </div>

          <template v-if="!isRenewPlanYearForm && planYearData">
            <input
              id="planStartDatePQ"
              type="text"
              class="form-control text-16 plan-start-date-input"
              @change="handleFieldChange"
              :class="{
                'is-invalid':
                  !isFieldValid('plan_start_date') && isFieldTouched('plan_start_date')
              }"
              v-model="planYearData.plan_start_date"
              v-maska="rawMaskVal"
              data-maska="##/##/####" />
          </template>
        </div>
      </div>
    </template>

    <!-- Dynamic questions -->
    <div
      class="mb-3"
      v-for="(question, questionIndex) in questions"
      :key="question.id">
      <div class="question-title text-14 mb-1">
        <strong>{{ question.question }}</strong>
      </div>
      <div class="row">
        <div :class="question.type > 1 ? 'custom-input-width' : 'col-12'">
          <div>
            <!-- Radio buttons -->
            <template v-if="question.type === 0">
              <div
                v-for="(option, optionIndex) in question.options"
                class="form-check form-check-inline"
                :key="optionIndex"
                @click="question.answer = optionIndex, handleFieldChange()">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="question.id + '' + optionIndex + 'radio'"
                  :name="questionIndex + 'radio'"
                  v-model="question.answer"
                  :value="optionIndex" />
                <label class="form-check-label text-16">
                  <TextWithDefinitions :text="option" />
                </label>
              </div>
            </template>
            
            <!-- Checkboxes -->
            <template v-else-if="question.type === 1">
              <div
                v-for="(option, optionIndex) in question.options"
                :key="optionIndex"
                @click="handleCheckboxChange(question, optionIndex)"
                class="form-check">
                <input
                  class="form-check-input text-16"
                  type="checkbox"
                  :id="option"
                  v-model="question.answer"
                  :value="optionIndex" />
                <label class="form-check-label text-16">
                  <TextWithDefinitions :text="option" />
                </label>
              </div>
            </template>
            
            <!-- Dropdown -->
            <div v-else-if="question.type === 2">
              <select
                :id="`dropdownPQ-${question.id}`"
                class="form-select text-16"
                :value="question.isOptional && (question.answer === null || question.answer === undefined) ? '' : question.answer"
                @change="handleDropdownChange(question, $event)">
                <option v-if="question.isOptional" value="">
                  Please select
                </option>
                <option v-for="(option, i) in question.options" :value="i" :key="i">
                  <TextWithDefinitions :text="option" />
                </option>
              </select>
            </div>
            
            <!-- Number input -->
            <div v-else-if="question.type === 3">
              <input
                :id="`numberInputPQ-${question.id}`"
                class="form-control text-16"
                type="number"
                @change="handleFieldChange"
                v-model="question.answer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.form-check-input {
  border: 1px solid var(--bs-gray-600, #6c757d) !important;
}

.question-title {
  flex-basis: 100%;
}

.custom-input-width {
  flex: 0 0 auto;
  max-width: 17.75rem;
  width: 17.75rem;
}
.tax-id-input {
  width: 16.25rem;
}
.plan-start-date-input {
  width: 11rem;
}
</style>
